<template>
  <div class="dentists">
    <div class="filters" uk-grid>
      <div class="uk-width-1-2@s uk-width-1-3@m">
        <div class="uk-card uk-card-small uk-card-default">
          <div class="uk-card-header uk-text-center">
            <h3 class="uk-card-title" style="font-size: 1.25rem">Location</h3>
          </div>
          <div class="uk-card-body">
            <label class="uk-form-label" for="postcode">Postcode</label>
            <div class="uk-flex">
              <input id="postcode" v-model="postcode" class="uk-input" type="text" name="postcode" data-cy="input-postcode" @keyup.enter="getPostcodeInfo">
              <button class="uk-button uk-button-primary" data-cy="button-postcode" @click="getPostcodeInfo">Search</button>
            </div>
          </div>
        </div>
      </div>

      <div class="uk-width-1-2@s uk-width-2-3@m">
        <div class="uk-card uk-card-small uk-card-default">
          <div class="uk-card-header uk-text-center">
            <h3 class="uk-card-title" style="font-size: 1.25rem">Filters</h3>
          </div>
          <div class="uk-card-body">
            <div class="uk-grid-small" uk-grid>
              <div class="uk-width-1-3">
                <label class="uk-form-label" for="distance">Distance (miles)</label>
                <div class="uk-inline debounce-wrapper">
                  <span class="uk-form-icon uk-form-icon-flip" v-html="$icons.refresh" />
                  <input id="distance" v-model.lazy="filter.distance" v-debounce="600" class="uk-input" type="number" min="0" name="distance" placeholder="No Limit">
                </div>
              </div>

              <div class="uk-width-1-3">
                <label class="uk-form-label" for="date">Date</label>
                <multiselect id="date" v-model="filter.date" :options="optionsDate" :show-labels="false" track-by="id" placeholder="Select date" :allow-empty="false" data-cy="filter-date" @select="selectDate">
                  <template slot="singleLabel" slot-scope="props">
                    {{ props.option.h }}
                  </template>
                  <template slot="option" slot-scope="props">
                    <div class="uk-flex uk-flex-between">
                      <div>{{ props.option.h }}</div>
                      <div>{{ props.option.day }}</div>
                    </div>
                  </template>
                </multiselect>
              </div>

              <div class="uk-width-1-3">
                <label class="uk-form-label" for="time">Time</label>
                <multiselect id="time" v-model="filter.time" :options="optionsTime" :show-labels="false" track-by="id" placeholder="Select time" :allow-empty="false" data-cy="filter-time" @select="selectTime">
                  <template slot="singleLabel" slot-scope="props">
                    {{ props.option.time.short }}
                  </template>
                  <template slot="option" slot-scope="props">
                    {{ props.option.time.short }}
                  </template>
                </multiselect>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <transition name="fade" mode="out-in">
      <template v-if="flag.search === 0">
        <div key="prompt">
          <div class="uk-alert-primary uk-text-center uk-padding-small uk-margin-medium-top uk-width-large uk-margin-auto" uk-alert>
            <div class="uk-text-bold">Please input a postcode and press search.</div>
            <div class="uk-text-small">No postcode selected.</div>
          </div>
        </div>
      </template>
      <template v-if="flag.search === 1">
        <div key="prompt">
          <div class="uk-alert-primary uk-text-center uk-padding-small uk-margin-medium-top uk-width-large uk-margin-auto" uk-alert>
            <div class="uk-text-bold uk-margin-bottom">Searching for dentists matching your criteria.</div>
            <div uk-spinner />
          </div>
        </div>
      </template>
      <template v-if="flag.search === 2 && filteredDentists.length > 0">
        <transition-group name="list" tag="div" class="results">
          <div v-for="dentist in filteredDentists" :key="dentist.id" class="list-item">
            <div class="uk-card uk-card-small uk-card-default">
              <div class="uk-card-header uk-card-primary uk-text-center" :class="featuresClass(dentist.features)">
                <h3 class="uk-card-title" style="font-size: 1.25rem">{{ dentist.name }}</h3>
              </div>
              <div class="uk-card-body">
                <div>
                  <b>Address:</b>
                  <p class="uk-margin-remove">
                    {{ dentist.location.house }} {{ dentist.location.street }}<br>
                    {{ dentist.location.postcode }}<br>
                    {{ dentist.location.city }}
                  </p>
                </div>

                <hr>

                <div>
                  <b>Approximate Distance</b>: <span>{{ dentist.distance | round }} miles</span>
                  <!-- <span>{{ dentist.distance | mtokm | round }} km</span> -->
                </div>

                <hr>

                <div class="uk-text-center">
                  <b>Features</b>

                  <div class="features">
                    <div v-for="feature in $helpers.practiceFeatures" :key="feature.id" class="feature" :class="(dentist.features[feature.id] && dentist.features[feature.id].status) ? 'available' : 'unavailable'" :uk-tooltip="(dentist.features[feature.id] && dentist.features[feature.id].notes) ? dentist.features[feature.id].notes : null">
                      <div class="title">{{ feature.value }}</div>
                      <div v-if="dentist.features[feature.id] && dentist.features[feature.id].notes" class="notes"><div class="icon gg-file-document" /></div>
                    </div>
                  </div>
                </div>

                <hr>

                <div class="uk-text-center">
                  <b>Availability</b>

                  <div v-if="dentist.availability.length > 5" class="carousel">
                    <hooper :ref="`ac${dentist.id}`" :settings="hooperSettings" @loaded="centerAvailability(filter.time.id)">
                      <hooper-slide v-for="(bit, index) in dentist.availability" :key="index" :index="index" :data-key="bit.id" :data-index="index">
                        <div class="bit" :class="{ selected: (selected.dentist === dentist.id && selected.bit === bit.id) }" data-cy="availability-bit" @click="selectBit(dentist.id, bit.id)">{{ bit.time.short }}</div>
                      </hooper-slide>
                    </hooper>

                    <div class="controls">
                      <button class="uk-button uk-button-primary icon-button control-left" @click="$refs[`ac${dentist.id}`][0].slidePrev()"><div class="icon gg-chevron-left" /></button>
                      <button class="uk-button uk-button-primary icon-button control-right" @click="$refs[`ac${dentist.id}`][0].slideNext()"><div class="icon gg-chevron-right" /></button>
                    </div>
                  </div>

                  <div v-else class="bit-list">
                    <template v-for="bit in dentist.availability">
                      <div :key="bit.id" class="bit" :class="{ selected: (selected.dentist === dentist.id && selected.bit === bit.id) }" @click="selectBit(dentist.id, bit.id)">{{ bit.time.short }}</div>
                    </template>
                  </div>
                </div>

                <hr>

                <div class="uk-text-center">
                  <aggregated-availability :dentist="dentist.id" />
                </div>
              </div>
              <div class="uk-card-footer uk-text-center">
                <button class="uk-button uk-button-primary" :disabled="selected.dentist !== dentist.id" data-cy="availability-select" @click="pick(dentist.id)">Select</button>
              </div>
            </div>
          </div>
        </transition-group>
      </template>
      <template v-if="flag.search === 2 && filteredDentists.length === 0 && !location.error">
        <div key="error">
          <div class="uk-alert-warning uk-text-center uk-padding-small uk-margin-medium-top uk-width-large uk-margin-auto" uk-alert>
            <div class="uk-text-bold">There are no dentists available for selected criteria.</div>
            <div class="uk-text-small">No dentists matching the search parameters.</div>
          </div>
        </div>
      </template>
    </transition>

    <div id="confirm-modal" uk-modal="bg-close: false; esc-close: false;">
      <div class="uk-modal-dialog">
        <div class="uk-modal-header">
          <h4 class="uk-text-center">Please confirm the details of the selected appointment:</h4>
        </div>
        <div class="uk-modal-body uk-text-center">
          <div class="confirm">
            <div v-if="selected.dentist" class="section">
              <div class="section-value">
                {{ $store.getters['dentists/find'](selected.dentist).name }} - {{ $store.getters['dentists/find'](selected.dentist).location.city }}
              </div>
              <div class="section-label">Practice</div>
            </div>
            <div v-if="selected.date" class="section">
              <div class="section-value">{{ selected.date.h }}</div>
              <div class="section-label">Date</div>
            </div>
            <div v-if="selected.bit" class="section">
              <div class="section-value">{{ $helpers.timeBits.find(b => b.id === selected.bit).time.long }}</div>
              <div class="section-label">Time</div>
            </div>
          </div>
        </div>
        <div class="uk-modal-footer uk-text-center">
          <button class="uk-button uk-button-danger uk-modal-close" type="button">Cancel</button>
          <button class="uk-button uk-button-primary uk-margin-left" type="button" data-cy="confirm-selection" @click="select()">Confirm</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import nowMixin from '@/utils/mixins/now'
import AggregatedAvailability from '@/components/AggregatedAvailability.vue'

export default {
  name: 'ComponentAppointmentDentist',

  components: { AggregatedAvailability },

  filters: {
    // Round to 2 decimal places
    round: (value) => {
      if (!value) return ''
      return Math.round(value * 100) / 100
    },

    // Convert Kilometers to Miles
    mtokm: (value) => {
      if (!value) return ''
      return value * 1.609344
    }
  },

  mixins: [nowMixin],

  props: {
    customerLocation: {
      type: String,
      required: false,
      default: null
    },
    features: {
      type: Array,
      required: false,
      default() {
        return []
      }
    }
  },

  data() {
    return {
      modal: null,
      flag: {
        search: 0
      },
      location: {
        postcode: '',
        info: null,
        error: null
      },
      filter: {
        distance: 50,
        date: null,
        time: null
      },
      hooperSettings: {
        itemsToShow: 5,
        itemsToSlide: 4,
        centerMode: true,
        trimWhiteSpace: true
      },
      selected: {
        dentist: null,
        bit: null,
        date: null
      }
    }
  },

  computed: {
    postcode: {
      get() { return this.location.postcode },
      set(text) { this.location.postcode = text.toUpperCase() }
    },

    dentists() {
      return this.$store.state.dentists.collection
    },

    optionsTime() {
      let r = null

      if (this.filter.date.t === this.now.date.t) {
        r = this.$helpers.timeBits.filter((bit) => bit.id >= this.now.time.id)
      } else {
        r = this.$helpers.timeBits
      }

      return r
    },

    optionsDate() {
      // This roundabout approach is required due to Vue's reactivity
      const start = this.$moment(this.now.date.t, 'YYMMDD')
      const end = this.$moment(this.now.date.t, 'YYMMDD').add(6, 'day')
      // Get range of last 7 dates with moment and moment-range
      const range = this.$moment.range(start, end)
      // Convert range to Array
      const rangeArray = Array.from(range.by('day'))
      // Format Array data to match data structure
      return rangeArray.map((v) => ({
        id: v.format('E'),
        h: v.format('DD/MM/YYYY'), // human-readable
        t: v.format('YYMMDD'), // technical
        day: v.format('dddd')
      }))
    },

    filteredDentists() {
      // Make a non-reactive copy
      let dentists = []
      this.dentists.forEach((dentist) => {
        dentists.push({
          id: dentist.id,
          availability: dentist.availability,
          name: dentist.name,
          location: dentist.location,
          features: dentist.features,
          status: dentist.status
        })
      })

      if (!this.location.info || this.location.error) {
        dentists = []
      }

      // Account Status Filter - Filter out Suspended Dentists
      dentists = dentists.filter((dentist) => dentist.status === 1)

      // Distance Calculation
      if (this.location.info && !this.location.error) {
        dentists = dentists.map((dentist) => ({
          ...dentist,
          distance: this.getDistance(dentist.location.geopoint.latitude, dentist.location.geopoint.longitude, this.location.info.latitude, this.location.info.longitude)
        }))
      }

      // Distance Filter
      if (this.location.info && !this.location.error && this.filter.distance) {
        dentists = dentists.filter((dentist) => dentist.distance < this.filter.distance)
      }

      // Availability Formatting
      // eslint-disable-next-line no-restricted-syntax
      for (const dentist of dentists) {
        let available = []
        const availability = dentist.availability[this.filter.date.t]
        // Include only available bit
        // eslint-disable-next-line no-restricted-syntax
        for (const key in availability) {
          if (availability[key] === 1) {
            available.push(this.$helpers.timeBits.find((b) => b.id === parseInt(key, 10)))
          }
        }
        // If selected date is today, then remove slots from before current time
        available = (this.filter.date.t === this.now.date.t) ? available.filter((b) => b.id >= this.now.time.id) : available
        // Filter by Time
        // available = available.filter(b => b.id >= this.filter.time.id - 2)
        dentist.availability = available
      }

      // Remove dentists with no available bits for selected date
      dentists = dentists.filter((dentist) => dentist.availability.length)

      return dentists
    }
  },

  created() {
    this.filter.date = this.now.date
    this.filter.time = this.now.time

    if (this.customerLocation) {
      this.location.postcode = this.customerLocation
    }
  },

  mounted() {
    this.modal = this.$uikit.modal('#confirm-modal')
  },

  methods: {
    async getPostcodeInfo() {
      const location = `https://api.postcodes.io/postcodes/${this.location.postcode}`
      this.location.info = null
      this.location.error = null
      this.flag.search = 1

      try {
        const response = await fetch(location)

        if (response.ok) {
          const info = await response.json()
          this.location.error = null
          this.location.info = info.result
        } else {
          const info = await response.json()
          this.location.info = null
          this.location.error = info.status
          this.$bus.$emit('appointment-error', { type: 'postcode-api', message: info.error, status: 'danger' })
        }
      } catch (error) {
        this.location.error = error
        this.$bus.$emit('appointment-error', { type: 'postcode-api', message: error, status: 'danger' })
      }

      setTimeout(() => {
        this.flag.search = 2
      }, 1000)
    },

    toRadians(v) { return (v * Math.PI) / 180 },
    toDegrees(v) { return (v * 180) / Math.PI },

    getDistance(lat1, lon1, lat2, lon2) {
      // Haversine formula
      const R = 6371e3 // metres
      const φ1 = this.toRadians(lat1)
      const φ2 = this.toRadians(lat2)
      const Δφ = this.toRadians((lat2 - lat1))
      const Δλ = this.toRadians((lon2 - lon1))

      const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2)
              + Math.cos(φ1) * Math.cos(φ2)
              * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

      const d = R * c

      return d / 1000 / 1.609344 // Convert KM to Miles
    },

    selectDate(selectedValue) {
      // Change filter time to next available bit if filter date is today
      if (selectedValue.t === this.now.date.t && this.filter.time.id < this.now.time.id) {
        this.filter.time = this.now.time
      }

      setTimeout(() => {
        this.centerAvailability(this.filter.time.id)
      }, 250)

      // this.resetSelection()
    },

    findClosest(array, target) {
      return array.reduce((prev, curr) => (Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev))
    },

    centerAvailability(target) {
      Object.keys(this.$refs).filter((ref) => {
        if (ref.includes('ac')) {
          return true
        }
        return false
      })
        .map((i) => this.$refs[i])
        .forEach((hooper) => {
          const list = hooper[0].$el.querySelector('.hooper-track')
          const key = this.findClosest([...list.children].map((i) => i.dataset.key), target)
          const { index } = list.querySelector(`[data-key='${key}']`).dataset
          hooper[0].slideTo(index)
          // const carousel = hooper[0]
          // carousel.currentSlide = parseInt(index, 10) + 2
        })
    },

    selectTime(selectedValue) {
      this.centerAvailability(selectedValue.id)

      // Reset selection if selected time is before filter time
      if (selectedValue.id > this.selected.bit) {
        this.resetSelection()
      }
    },

    resetSelection() {
      this.selected.dentist = null
      this.selected.date = null
      this.selected.bit = null
    },

    selectBit(dentist, bit) {
      if (this.selected.dentist === dentist && this.selected.bit === bit) {
        this.selected.dentist = null
        this.selected.bit = null
        this.selected.date = null
      } else {
        this.selected.dentist = dentist
        this.selected.bit = bit
        this.selected.date = this.filter.date
      }
    },

    pick(dentist) {
      if (this.selected.dentist === dentist) {
        this.modal.show()
      }
    },

    select() {
      this.modal.hide()

      const data = {
        selected: this.selected,
        location: this.location
      }

      setTimeout(() => {
        this.$bus.$emit('appointment-section-complete', { type: 'dentist', data })
      }, 150)
    },

    featuresClass(features) {
      let match = 0
      let c = null // class
      const target = this.features.length

      this.features.forEach((feature) => {
        if (features[feature] && features[feature].status) {
          match += 1
        }
      })

      if (target) {
        if (match === 0) {
          c = 'fnm'
        }

        if (match === target) {
          c = 'ffm'
        }

        if (match > 0 && match < target) {
          c = 'fpm'
        }
      }

      return c
    }
  }
}
</script>

<style lang="scss">
.filters .multiselect__option--selected.multiselect__option--highlight {
  background-color: $colour-primary;
}

.results {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 15px -15px 0;

  .list-item {
    display: inline-block;
    width: 380px;
    padding: 15px;
    transition: all 1s;
  }

  .features {
    display: flex;
    justify-content: center;

    [uk-tooltip] {
      cursor: help !important;
    }

    .feature {
      display: flex;
      justify-items: center;
      margin: 2px;
      background-color: $colour-secondary;
      color: $colour-light;
      cursor: default;

      .title,
      .notes {
        display: flex;
        padding: 10px;
      }

      .notes {
        padding-left: 0;
      }

      &.available {
        background-color: $colour-success;
        color: $colour-light;
      }

      &.unavailable {
        background-color: $colour-danger;
        color: $colour-light;
      }
    }
  }

  .bit {
    padding: 6px 4px;
    transition: background-color 100ms ease;
    border-right: 1px solid #fff;
    border-left: 1px solid #fff;
    background-color: #e5e5e5;
    cursor: pointer;

    &:hover,
    &.selected {
      background-color: $colour-primary;
      color: $colour-light;
    }

    &.selected {
      &:hover {
        background-color: darken( $colour-primary, 10% );
      }
    }
  }

  .bit-list {
    display: flex;
    justify-content: center;

    .bit {
      box-sizing: border-box;
      width: 20%;
    }
  }

  .carousel .hooper,
  .bit-list {
    width: calc(100% - 74px);
    height: 36px;
    margin: 6px auto 0;
    outline: none;
  }

  .carousel {
    position: relative;

    .controls {
      position: absolute;
      top: 50%;
      width: 100%;
      transform: translateY(-50%);

      .control-left,
      .control-right {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
      }

      .control-left {
        left: 0;
      }

      .control-right {
        right: 0;
      }
    }
  }
}

.confirm {
  display: inline-flex;
  border: 1px solid $colour-grey;

  .section {
    text-align: center;

    &:not(:last-of-type) {
      border-right: 1px solid $colour-grey;
    }

    .section-label {
      padding: 6px;
      border-top: 1px solid $colour-grey;
      background-color: $colour-primary;
      color: $colour-light;
      font-size: .7em;
      text-transform: uppercase;
      white-space: nowrap;
    }

    .section-value {
      padding: 6px 12px;
    }
  }
}

input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

// List Transition
.list-enter, .list-leave-to {
  opacity: 0;
}

.list-leave-active {
  position: absolute;
}


.debounce-wrapper {
  .uk-form-icon {
    transition: opacity 100ms ease;
    animation: rotating 2s linear infinite;
    opacity: 0;
  }

  &.debounce .uk-form-icon {
    opacity: 1;
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Features Partial Match
.uk-card-primary.fpm {
  background-color: $colour-warning;

  .uk-card-title {
    color: $colour-secondary;
  }
}

// Features Full Match
.uk-card-primary.ffm {
  background-color: $colour-success;
}

// Features No Match
.uk-card-primary.fnm {
  background-color: $colour-danger;
}
</style>
